import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Bot, Home as HomeIcon, Building2, ArrowRight, ShieldCheck, Award, MapPin, Star, Users, Sparkles, ThumbsUp, Upload, PhoneCall } from "lucide-react";
import { api } from "@/lib/api";
import { PHONE, TEL } from "@/lib/config";
import { useContent } from "@/lib/ContentContext";
import { SectionHeading, PrimaryBtn, OutlineBtn, CTAGroup, TrustStrip, Overline, FinancingNote } from "@/components/site/shared";
import { BeforeAfter } from "@/components/site/BeforeAfter";
import CustomerReviews from "@/components/site/CustomerReviews";
import { openGhost } from "@/components/site/GhostAI";
import { resolveImage } from "@/lib/api";

const HERO = "/gallery/hero.jpg";

const WHY = [
  { icon: ShieldCheck, t: "Veteran-Owned Business" }, { icon: Award, t: "Licensed & Insured" },
  { icon: Building2, t: "Residential & Commercial" }, { icon: Sparkles, t: "Professional Exterior Cleaning" },
  { icon: MapPin, t: "Local Orlando Service" }, { icon: ThumbsUp, t: "Free Quote Requests" },
  { icon: Star, t: "Real Customer Reviews" }, { icon: Users, t: "Proven Before-and-After Results" },
];

const QuickQuote = () => {
  const nav = useNavigate();
  const [q, setQ] = useState({ name: "", phone: "", service: "", zip: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!q.name || !q.phone) { toast.error("Please add your name and phone."); return; }
    setBusy(true);
    try {
      const r = await api.post("/leads", {
        name: q.name, phone: q.phone, service: q.service, zip: q.zip, city: "Orlando",
        source: "quick_quote", landing_page: "/",
      });
      nav(`/quote-confirmation?id=${r.data.lead_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Please try again."); setBusy(false); }
  };
  const ic = "h-12 px-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[var(--action)] w-full";
  return (
    <form onSubmit={submit} data-testid="quick-quote-strip"
      className="bg-white rounded-2xl card-shadow-lg border border-gray-100 p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
      <input className={ic} placeholder="Your name" value={q.name} onChange={(e) => setQ({ ...q, name: e.target.value })} data-testid="qq-name" />
      <input className={ic} placeholder="Phone" type="tel" value={q.phone} onChange={(e) => setQ({ ...q, phone: e.target.value })} data-testid="qq-phone" />
      <select className={ic} value={q.service} onChange={(e) => setQ({ ...q, service: e.target.value })} data-testid="qq-service">
        <option value="">Service needed</option>
        <option>Commercial Soft-Wash</option><option>Paver Sealing</option><option>High-Pressure Washing</option>
        <option>Driveway & Concrete Cleaning</option><option>Pool Area Cleaning</option>
      </select>
      <input className={ic} placeholder="ZIP code" value={q.zip} onChange={(e) => setQ({ ...q, zip: e.target.value })} data-testid="qq-zip" />
      <button type="submit" disabled={busy} data-testid="qq-submit"
        className="btn-lift bg-[var(--red)] hover:bg-[var(--red-dark)] text-white font-semibold rounded-xl h-12 disabled:opacity-60">
        {busy ? "Sending…" : "Get My Free Quote"}
      </button>
    </form>
  );
};

export default function Home() {
  const { content } = useContent();
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const featured = projects.filter((p) => p.before_image && p.after_image).slice(0, 2);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center" data-testid="hero-section">
        <img src={content.homepage_hero_image ? resolveImage(content.homepage_hero_image) : HERO}
          alt="Professional power washing in Orlando"
          className={`absolute inset-0 w-full h-full object-cover ${content.homepage_hero_mobile_image ? "hidden sm:block" : ""}`}
          style={{ objectPosition: content.homepage_hero_position || "center" }} />
        {content.homepage_hero_mobile_image && (
          <img src={resolveImage(content.homepage_hero_mobile_image)} alt=""
            className="absolute inset-0 w-full h-full object-cover sm:hidden"
            style={{ objectPosition: content.homepage_hero_mobile_position || content.homepage_hero_position || "center" }} />
        )}
        <div className="absolute inset-0 hero-overlay" />
        {content.homepage_hero_overlay > 0 && (
          <div className="absolute inset-0" style={{ background: `rgba(10,25,47,${Math.min(Number(content.homepage_hero_overlay), 80) / 100})` }} />
        )}
        <div className="relative max-w-7xl mx-auto px-4 py-20 w-full">
          <div className="max-w-3xl fade-up">
            <span className="inline-flex items-center gap-2 bg-[var(--red)] text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide">
              <ShieldCheck className="w-4 h-4" /> Veteran-Owned · Orlando, FL
            </span>
            <h1 className="font-head font-extrabold uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-5 tracking-tight">
              {content.hero_headline || "Veteran-Owned Exterior Cleaning You Can Count On"}
            </h1>
            <p className="text-white/85 text-base sm:text-lg mt-5 max-w-2xl leading-relaxed">
              {content.hero_subtext || "Professional power washing, soft washing, paver sealing, and exterior cleaning for residential and commercial properties throughout the Orlando area."}
            </p>
            <div className="mt-8"><CTAGroup ghost={false} /></div>
            <div className="mt-8"><TrustStrip light /></div>
          </div>
        </div>
      </section>

      {/* CUSTOMER REVIEWS — social proof directly under the hero */}
      <CustomerReviews mode="home" />

      {/* QUICK QUOTE */}
      <section className="pt-2 pb-4">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-3"><Overline className="text-[var(--red)]">Fast & Free · No Obligation</Overline></div>
          <QuickQuote />
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-pad" data-testid="services-overview">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeading overline="What We Do" title="Professional Cleaning Services"
            subtitle="From driveways to commercial buildings, we restore your property's curb appeal with the right method for every surface." center />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <div key={s.id} className="group bg-white rounded-2xl overflow-hidden border border-gray-100 card-shadow btn-lift" data-testid={`service-card-${s.slug}`}>
                <div className="h-48 overflow-hidden">
                  <img src={resolveImage(s.image)} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <h3 className="font-head font-bold text-xl text-[var(--navy)] uppercase tracking-tight">{s.name}</h3>
                  <p className="text-sm text-[var(--text-muted)] mt-2 leading-relaxed">{s.short_description}</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Link to={`/services/${s.slug}`} className="text-sm font-semibold text-[var(--action)] hover:underline flex items-center gap-1">Learn More <ArrowRight className="w-4 h-4" /></Link>
                    <Link to={`/get-a-quote?service=${encodeURIComponent(s.name)}`} className="text-sm font-semibold text-[var(--red)] hover:underline">Request Quote</Link>
                    <button onClick={openGhost} className="text-sm font-semibold text-[var(--navy)] hover:underline">Ask Ghost AI</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE & AFTER */}
      {featured.length > 0 && (
        <section className="section-pad bg-[var(--navy)]" data-testid="before-after-section">
          <div className="max-w-7xl mx-auto px-4">
            <SectionHeading overline="Real Results" title="See The Transformation"
              subtitle="Drag the slider to see the difference on real projects." light />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {featured.map((p) => (
                <div key={p.id}>
                  <div className="aspect-[4/3]"><BeforeAfter before={p.before_image} after={p.after_image} alt={p.title} /></div>
                  <p className="text-white font-semibold mt-3">{p.title} <span className="text-white/50 font-normal">· {p.category}</span></p>
                </div>
              ))}
            </div>
            <div className="mt-10 text-center">
              <PrimaryBtn to="/before-after">View More Transformations <ArrowRight className="w-4 h-4" /></PrimaryBtn>
            </div>
          </div>
        </section>
      )}

      {/* WHY CHOOSE US */}
      <section className="section-pad" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeading overline="Why Choose Us" title="Trusted, Local & Professional" center />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {WHY.map((w) => (
              <div key={w.t} className="bg-[var(--bg-light)] border border-gray-100 rounded-2xl p-6 text-center btn-lift">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-[var(--action)]/10 flex items-center justify-center mb-4">
                  <w.icon className="w-7 h-7 text-[var(--action)]" />
                </div>
                <p className="font-semibold text-[var(--navy)] text-sm">{w.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PATHWAYS */}
      <section className="pb-4">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="relative rounded-3xl overflow-hidden p-8 sm:p-10 text-white min-h-[300px] flex flex-col justify-end" data-testid="pathway-residential"
               style={{ background: "linear-gradient(0deg, rgba(10,25,47,0.92), rgba(10,25,47,0.5)), url('https://images.unsplash.com/photo-1704457030816-6b9b08d1bb08?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000') center/cover" }}>
            <HomeIcon className="w-9 h-9 text-[var(--aqua)] mb-3" />
            <h3 className="font-head font-extrabold text-3xl uppercase tracking-tight">Residential Exterior Cleaning</h3>
            <p className="text-white/85 mt-2">{content.residential_intro || "Improve curb appeal and remove built-up dirt, algae, grime, and stains from exterior surfaces around your home."}</p>
            <div className="mt-5"><PrimaryBtn to="/residential">Explore Residential Services <ArrowRight className="w-4 h-4" /></PrimaryBtn></div>
          </div>
          <div className="relative rounded-3xl overflow-hidden p-8 sm:p-10 text-white min-h-[300px] flex flex-col justify-end" data-testid="pathway-commercial"
               style={{ background: "linear-gradient(0deg, rgba(0,73,110,0.94), rgba(0,73,110,0.5)), url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000') center/cover" }}>
            <Building2 className="w-9 h-9 text-[var(--aqua)] mb-3" />
            <h3 className="font-head font-extrabold text-3xl uppercase tracking-tight">Commercial Exterior Cleaning</h3>
            <p className="text-white/85 mt-2">{content.commercial_intro || "Professional exterior cleaning for businesses and commercial properties that need a clean, welcoming appearance."}</p>
            <div className="mt-5"><OutlineBtn to="/commercial" className="!bg-white">Request Commercial Service <ArrowRight className="w-4 h-4" /></OutlineBtn></div>
          </div>
        </div>
      </section>

      {/* OWNER */}
      <section className="section-pad bg-[var(--bg-light)]" data-testid="owner-section">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="rounded-3xl overflow-hidden aspect-[4/5] bg-[var(--navy)] flex items-center justify-center">
            {content.owner_photo ? <img src={resolveImage(content.owner_photo)} alt="Owner" className="w-full h-full object-cover" />
              : <img src="/brand/logo.png" alt="Veterans Power Wash" className="w-3/4 h-3/4 object-contain" />}
          </div>
          <div>
            <Overline className="text-[var(--red)]">Meet The Owner</Overline>
            <h2 className="font-head font-extrabold uppercase text-3xl sm:text-4xl text-[var(--navy)] tracking-tight mt-2">Veteran-Owned. Locally Committed.</h2>
            <p className="text-[var(--text-muted)] mt-4 leading-relaxed">{content.owner_story}</p>
            <div className="mt-6"><PrimaryBtn to="/about">Learn More About Us <ArrowRight className="w-4 h-4" /></PrimaryBtn></div>
          </div>
        </div>
      </section>

      {/* GHOST AI */}
      <section className="section-pad" data-testid="ghost-ai-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-[var(--navy)] rounded-3xl p-8 sm:p-12 text-white grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-center">
            <img src="/brand/ghost.png" alt="Ghost AI" className="h-28 w-28 rounded-2xl bg-white/5 p-3 mx-auto" />
            <div>
              <h2 className="font-head font-extrabold uppercase text-3xl sm:text-4xl tracking-tight">Get Help Instantly with Ghost AI</h2>
              <p className="text-white/75 mt-3">Ghost AI can help you choose a service, request a free quote, upload photos, or schedule a callback.</p>
              <div className="flex flex-wrap gap-3 mt-6">
                <button onClick={openGhost} className="btn-lift bg-[var(--red)] hover:bg-[var(--red-dark)] text-white font-semibold px-6 py-3 rounded-full flex items-center gap-2"><Bot className="w-4 h-4" /> Start a Conversation</button>
                <button onClick={openGhost} className="btn-lift bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-full flex items-center gap-2"><Upload className="w-4 h-4" /> Upload Photos</button>
                <button onClick={openGhost} className="btn-lift bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-full flex items-center gap-2"><PhoneCall className="w-4 h-4" /> Request a Callback</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="pb-16" data-testid="service-area-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <MapPin className="w-10 h-10 text-[var(--action)] mx-auto" />
          <h2 className="font-head font-extrabold uppercase text-3xl text-[var(--navy)] tracking-tight mt-3">Serving Orlando, Florida</h2>
          <p className="text-[var(--text-muted)] mt-3">{content.service_area_note || "Serving Orlando and surrounding areas. Contact us to confirm service availability for your property."}</p>
          <div className="mt-6"><OutlineBtn to="/service-areas">View Service Areas</OutlineBtn></div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-20" data-testid="final-cta"
        style={{ background: "linear-gradient(115deg, #0A192F, #00496e)" }}>
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="font-head font-extrabold uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight">Ready to Restore Your Property's Curb Appeal?</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <PrimaryBtn to="/get-a-quote">Get a Free Quote</PrimaryBtn>
            <a href={TEL} className="btn-lift bg-white text-[var(--navy)] font-semibold px-6 py-3 rounded-full flex items-center gap-2"><Phone className="w-4 h-4 text-[var(--red)]" /> Call {PHONE}</a>
            <OutlineBtn onClick={openGhost} className="!bg-white/10 !border-white/30 !text-white"><Bot className="w-4 h-4" /> Ask Ghost AI</OutlineBtn>
          </div>
          <div className="max-w-md mx-auto mt-8"><FinancingNote className="!bg-white/10 !border-white/20 text-left [&_p]:text-white/80" /></div>
        </div>
      </section>
    </div>
  );
}
